import React, { FC, useMemo, useEffect } from 'react';
import { Box } from '@wix/design-system';
import {
  APP_STATUS,
  DEFAULT_FILTERS,
  useTemplatesContext,
  ACTIONS,
} from '@components/Contexts/TemplatesContext';
import GalleryGridMobile from '../GalleryGridMobile';
import { useInfiniteTemplatesQuery } from '@components/Hooks/useInfiniteTemplatesQuery';
import TemplateGalleryErrorScreen from '../TemplateGalleryErrorState';
import useTemplateSearchField from '@components/Templates/TemplateSearchField/useTemplateSearchField';
import {
  EMPTY_STATE_BANNER_TYPES,
  EmptyStateBannerWrapper,
} from '@components/Templates/EmptyStates/EmptyStateBannerWrapper/EmptyStateBannerWrapper';
import GalleryHeaderPopular from '../GalleryHeader/GalleryHeaderPopular';
import { useTranslation } from '@wix/fe-essentials-standalone/i18n';
import GalleryHeader from '../GalleryHeader';
import { getSliceOfTemplates } from '@utils/queryResponseUtils';
import TemplateGalleryEmptyFooter from '../TemplateGalleryEmptyFooter';
import FeaturedTemplatesMobile from '@components/Templates/TemplateGallery/TemplateGalleryMobile/FeaturedTemplatesMobile/FeaturedTemplatesMobile';
import s from '../TemplateGallery.module.scss';
import sMobile from './TemplateGalleryMobile.module.scss';

const TemplateGalleryMobile: FC = () => {
  const { t } = useTranslation();
  const {
    state: { filters, sort, appStatus },
    dispatch,
  } = useTemplatesContext();
  const userQueryResponse = useInfiniteTemplatesQuery(filters, sort);
  const { data: userQueryData, hasNextPage, fetchNextPage } = userQueryResponse;
  const { data: emptyStateQueryData } = useInfiniteTemplatesQuery(
    DEFAULT_FILTERS,
    'purchases',
  );
  const { isSearchEnabled } = useTemplateSearchField();

  const templateCount = userQueryData?.pages?.[0]?.count;

  useEffect(() => {
    dispatch({
      type: ACTIONS.SET_NUMBER_OF_RESULTS,
      payload: templateCount ?? 0,
    });
  }, [templateCount, dispatch]);

  const showGeneralError = appStatus === APP_STATUS.ERROR;
  const showEmptyError = templateCount === 0;

  const showErrorScreen =
    (!isSearchEnabled && showEmptyError) || showGeneralError;

  const showSearchEmptyStates = showEmptyError && isSearchEnabled;
  const emptyStateBannerType = Boolean(filters?.search)
    ? EMPTY_STATE_BANNER_TYPES.search
    : EMPTY_STATE_BANNER_TYPES.filter;

  const emptyStateTemplates = useMemo(
    () =>
      getSliceOfTemplates(emptyStateQueryData)?.pages?.flatMap(
        (page) => page?.templates || [],
      ) || [],
    [emptyStateQueryData],
  );

  return (
    <Box
      dataHook="templates-gallery-mobile-container"
      height="100%"
      minHeight="calc(100vh - 295px)"
      direction="vertical"
    >
      {showErrorScreen ? (
        <TemplateGalleryErrorScreen
          showEmptyError={showEmptyError}
          isMobileView
        />
      ) : (
        <Box
          dataHook="templates-gallery-mobile"
          direction="vertical"
          className={`${s.templateGalleryContainer} ${s.mobileView}`}
        >
          {showSearchEmptyStates && (
            <EmptyStateBannerWrapper type={emptyStateBannerType} isMobileView />
          )}
          {showSearchEmptyStates && (
            <GalleryHeaderPopular
              viewTextOverride={t(
                'marketplace.templates-page.empty-state.gallery-header.view-all-short',
              )}
              className={sMobile.mobileGalleryHeader}
            />
          )}
          {isSearchEnabled && !showSearchEmptyStates && (
            <GalleryHeader
              templateCount={templateCount}
              hasSortMenu={false}
              className={sMobile.mobileGalleryHeader}
            />
          )}
          {showSearchEmptyStates ? (
            <FeaturedTemplatesMobile templates={emptyStateTemplates} />
          ) : (
            <GalleryGridMobile
              templatesPages={userQueryData}
              hasNextPage={hasNextPage}
              fetchNextPage={fetchNextPage}
            />
          )}
          {showSearchEmptyStates && (
            <TemplateGalleryEmptyFooter
              className={sMobile.mobileGalleryFooter}
              textProps={{ size: 'small', weight: 'thin' }}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default TemplateGalleryMobile;
